import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AuthContext } from "../../../context/AuthContext";
import { SnackAlertContext } from "../../../context/SnackAlertContext";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import TimeSummary from "./TimeSummary";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc"; // ES 2015
import timezone from "dayjs/plugin/timezone"; // ES 2015
import isoWeek from "dayjs/plugin/isoWeek"; // ES 2015
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);
const DEFAULT_TIMEZONE = "America/Phoenix";
dayjs.tz.setDefault(DEFAULT_TIMEZONE);

pdfMake.vfs = pdfFonts.pdfMake.vfs;

// isoDate is "YYYY-MM-DD"
// the isoDateParam will always be treated as a Phoenix Date
const TimeApproval = ({ token = null }) => {
  const { weeklyTimeSummaryId } = useParams();
  const navigate = useNavigate();
  const { ezorder, authToken, decoded } = useContext(AuthContext);
  const { openSnackMessage } = useContext(SnackAlertContext);
  const [loading, setLoading] = useState(false);
  const [weeklyUserTimeSummary, setWeeklyUserTimeSummary] = useState();

  const getTimeSummary = async () => {
    try {
      setLoading(true);

      let response = await ezorder.get(
        `/user/time-summaries/${weeklyTimeSummaryId}`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.status == 200 || response.status === 201) {
        setWeeklyUserTimeSummary(response.data.weeklyUserTimeSummary);
      }
    } catch (error) {
      console.log(error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.log(error.response.data);
        console.log(error.response.status);
        console.log(error.response.headers);
        openSnackMessage("error", error.response.data.error);
      } else {
        // Something went really bad
        console.log(error);
        openSnackMessage("error", error);
      }
    } finally {
      setLoading(false);
    }
  };
  const getTimeSummaryNoAuth = async () => {
    try {
      setLoading(true);

      let response = await ezorder.get(
        `/no-auth/user/time-summaries/${weeklyTimeSummaryId}?token=${token}`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.status == 200 || response.status === 201) {
        setWeeklyUserTimeSummary(response.data.weeklyUserTimeSummary);
      }
    } catch (error) {
      console.log(error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.log(error.response.data);
        console.log(error.response.status);
        console.log(error.response.headers);
        openSnackMessage("error", error.response.data.error);
      } else {
        // Something went really bad
        console.log(error);
        openSnackMessage("error", error);
      }
    } finally {
      setLoading(false);
    }
  };

  const approveTime = async (pin) => {
    try {
      setLoading(true);

      let response = await ezorder.post(
        `/user/time-summaries/${weeklyTimeSummaryId}/approve`,
        { pin },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.status == 200 || response.status === 201) {
        openSnackMessage("success", "Time Card Signed");
        getTimeSummary();
      } else {
        openSnackMessage("error", response.response.data.error);
      }
    } catch (error) {
      console.log(error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.log(error.response.data);
        console.log(error.response.status);
        console.log(error.response.headers);
        openSnackMessage("error", error.response.data.error);
      } else {
        // Something went really bad
        console.log(error);
        openSnackMessage("error", error);
      }
    } finally {
      setLoading(false);
    }
  };
  const approveTimeNoAuth = async (pin) => {
    try {
      setLoading(true);

      let response = await ezorder.post(
        `/no-auth/user/time-summaries/${weeklyTimeSummaryId}/approve`,
        { pin, token }
      );

      if (response.status == 200 || response.status === 201) {
        openSnackMessage("success", "Time Card Signed");
        getTimeSummaryNoAuth();
      } else {
        openSnackMessage("error", response.response.data.error);
      }
    } catch (error) {
      console.log(error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.log(error.response.data);
        console.log(error.response.status);
        console.log(error.response.headers);
        openSnackMessage("error", error.response.data.error);
      } else {
        // Something went really bad
        console.log(error);
        openSnackMessage("error", error);
      }
    } finally {
      setLoading(false);
    }
  };

  const requestNewPin = async () => {
    try {
      setLoading(true);

      let response = await ezorder.post(
        `/auth/pin`,
        {},
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.status == 200 || response.status === 201) {
        openSnackMessage("success", "A new PIN Number was sent to your phone");
      } else {
        openSnackMessage("error", response.response.data.error);
      }
    } catch (error) {
      console.log(error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.log(error.response.data);
        console.log(error.response.status);
        console.log(error.response.headers);
        openSnackMessage("error", error.response.data.error);
      } else {
        // Something went really bad
        console.log(error);
        openSnackMessage("error", error);
      }
    } finally {
      setLoading(false);
    }
  };
  const requestNewPinNoAuth = async () => {
    try {
      setLoading(true);

      let response = await ezorder.post(
        `/no-auth/pin`,
        { token },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.status == 200 || response.status === 201) {
        openSnackMessage("success", "A new PIN Number was sent to your phone");
      } else {
        openSnackMessage("error", response.response.data.error);
      }
    } catch (error) {
      console.log(error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.log(error.response.data);
        console.log(error.response.status);
        console.log(error.response.headers);
        openSnackMessage("error", error.response.data.error);
      } else {
        // Something went really bad
        console.log(error);
        openSnackMessage("error", error);
      }
    } finally {
      setLoading(false);
    }
  };

  const createPresignedUrl = async (s3Key) => {
    try {
      let data = {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      };

      let keySplit = s3Key.split("/");
      let identifier = keySplit[0];
      let resource = keySplit[1];
      let fileName = keySplit[2];
      const response = await ezorder.post(
        "/files/presignedUrl",
        {
          identifier,
          resource,
          fileName,
          actionType: "READ",
          contentType: "application/pdf",
        },
        data
      );
      return response.data.getUrl;
    } catch (error) {
      // console.log(error);
      throw Error("Unable to load presigned URL", { cause: error });
    }
  };
  const downloadUploadedSignedSummary = async () => {
    let presignedUrl = await createPresignedUrl(
      weeklyUserTimeSummary.uploadedSignedSummary
    );

    // using Java Script method to get PDF file
    fetch(presignedUrl).then((response) => {
      response.blob().then((blob) => {
        console.log("BLOB", blob);
        // Creating new object of PDF file
        const fileURL = window.URL.createObjectURL(blob);
        // Setting various property values
        let alink = document.createElement("a");
        alink.href = fileURL;
        let fileExtension =
          weeklyUserTimeSummary.uploadedSignedSummary.split(".")[1];
        let fileName = `WEEKLY_TIME_SUMMARY_${dayjs(weeklyUserTimeSummary.start)
          .tz(DEFAULT_TIMEZONE)
          .startOf("isoWeek")
          .format("YYYY-MM-DD")} - ${dayjs(weeklyUserTimeSummary.start)
          .tz(DEFAULT_TIMEZONE)
          .endOf("isoWeek")
          .format("YYYY-MM-DD")}.${fileExtension}`;
        alink.download = fileName;
        alink.click();
      });
    });
  };

  const downloadUploadedSignedSummaryNoAuth = async () => {
    // Get Presigned URL with Token
    const presignedUrlResponse = await ezorder.post(
      "/no-auth/user/time-summaries/:timeSummaryId/presignedUrl",
      {
        token,
        uploadedSignedSummary: weeklyUserTimeSummary.uploadedSignedSummary,
      }
    );

    // using Java Script method to get PDF file
    fetch(presignedUrlResponse.key).then((response) => {
      response.blob().then((blob) => {
        console.log("BLOB", blob);
        // Creating new object of PDF file
        const fileURL = window.URL.createObjectURL(blob);
        // Setting various property values
        let alink = document.createElement("a");
        alink.href = fileURL;
        let fileExtension =
          weeklyUserTimeSummary.uploadedSignedSummary.split(".")[1];
        let fileName = `WEEKLY_TIME_SUMMARY_${dayjs(weeklyUserTimeSummary.start)
          .tz(DEFAULT_TIMEZONE)
          .startOf("isoWeek")
          .format("YYYY-MM-DD")} - ${dayjs(weeklyUserTimeSummary.start)
          .tz(DEFAULT_TIMEZONE)
          .endOf("isoWeek")
          .format("YYYY-MM-DD")}.${fileExtension}`;
        alink.download = fileName;
        alink.click();
      });
    });
  };

  useEffect(() => {
    if (token) {
      getTimeSummaryNoAuth();
    } else {
      getTimeSummary();
    }
  }, [weeklyTimeSummaryId, token]);

  if (!weeklyUserTimeSummary) return null;

  return (
    <TimeSummary
      token={token}
      weeklyUserTimeSummary={weeklyUserTimeSummary}
      approveTime={token ? approveTimeNoAuth : approveTime}
      requestNewPin={token ? requestNewPinNoAuth : requestNewPin}
      enableSignWithPin={true}
      loading={loading}
      downloadUploadedSignedSummary={
        token
          ? downloadUploadedSignedSummaryNoAuth
          : downloadUploadedSignedSummary
      }
      fileName={`WEEKLY_SUMMARY_${dayjs(weeklyUserTimeSummary.start)
        .tz(DEFAULT_TIMEZONE)
        .startOf("isoWeek")
        .format("YYYY-MM-DD")} - ${dayjs(weeklyUserTimeSummary.start)
        .tz(DEFAULT_TIMEZONE)
        .endOf("isoWeek")
        .format("YYYY-MM-DD")}`}
    />
  );
};

export default TimeApproval;
